import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import CustomCard from '../../../../Util/CustomCard';
import { useParams } from 'react-router-dom';
import { Dialog } from "primereact/dialog";
import { useDialogIsolation } from "../../../../Util/useDialogIsolation";
import { Button } from "primereact/button";
import styles from "../ServiceRequestDetails.module.css";
import formStyles from "../../ServiceRequest.module.css";
import { FaImage } from "react-icons/fa6";
import { MdDownload } from 'react-icons/md';
import { InputTextarea } from 'primereact/inputtextarea';
import { FloatLabel } from "primereact/floatlabel";
import { IoAdd } from "react-icons/io5";
import { getFileSize } from '../../../../Util/utility';
import { errorMessage } from "../../../../Util/errorMessage";
import { supportingDocumentError } from "../../../../Util/supportingDocument";
import { busyButtonProps } from "../../../../Util/busyButton";
import { useDialogSession, useFormMessages } from "../../../../Util/formSession";
import { tabStopWhileScrolling } from "../../../../Util/dataTable";
import SupportingDocumentUpload from "../../SupportingDocumentUpload";
import axios from 'axios';
import AuthContext from '../../../../../store/auth-context';
import { GlobalContext } from '../../../../../store/global-context';

// Q34 (ticket 21): Add new comment, brought to the journey's standard (v1.2 repeated the audit's RCMC-01 / -14 / -17 /
// -18 defects here). The one mandatory field, as Raise Service Request's (ticket 06, rulings Q25 / Q26 style): its
// element id, visible label and the error it shows when left empty
const COMMENT_FIELD = { id: "comment", label: "Comment", error: "Write your comment." };
// The attachment uses ticket 07's control and rules (Util/supportingDocument.js; the server's twin is
// UserPortal/supporting_document.py), under its own id and label
const ATTACHMENT_ID = "comment-attachment";

const EMPTY_COMMENT = { comment: "", attachment: "" };
// The Comments card's heading: it names the comment list's region
const COMMENTS_HEADING_ID = "comments-heading";

const Comments = (props) => {
    const { requestId } = useParams();
    const authCtx = useContext(AuthContext);
    const globalCtx = useContext(GlobalContext);
    const inputRef = useRef();
    const [isAddNewModalVisible,setIsAddNewModalVisible] = useState(false);
    // RCMC-23: page behind inert while open, focus to the first field; RCMC-02: named close button
    const commentDialogIsolation = useDialogIsolation(isAddNewModalVisible, "Add new comment");
    // Ticket 22 (Q35): true while a comment is on its way — Add stays focused and says it is busy
    const [isAddingComment,setIsAddingComment] = useState(false);
    const [newComment,setNewComment] = useState(EMPTY_COMMENT);
    // RCMC-18 / RCMC-01: error text for `comment` and `attachment`, set by a failed Add or a refused file
    const [fieldErrors, setFieldErrors] = useState({});
    // The control to focus after a failed Add (a new object each time, so a repeat Add refocuses)
    const [focusRequest, setFocusRequest] = useState(null);
    // Review of ticket 21 (shared since ticket 22, Util/formSession.js): a reply only touches the opening of the dialog
    // that sent it (closed and opened again mid-request, a late reply closed or marked the new dialog), and the form
    // takes away only the messages it raised: an error about "file" (a refused file) or the "form" (the summary, a
    // failed Add), and its "chosen" / "removed" confirmation
    const dialogSession = useDialogSession();
    const messages = useFormMessages();

    // Focus after the render that shows the error, so the control is read with "invalid" and its error text
    useEffect(() => {
        if (focusRequest) document.getElementById(focusRequest.id)?.focus();
    }, [focusRequest]);

    // Q34 (ticket 21, found by seam 2): the comments in the DOM oldest first, the order they show in. The API serves them
    // newest first, and v1.2 flipped them on screen with `flex-direction: column-reverse`, so a screen reader read them,
    // and Tab reached their downloads, in the opposite order to the one on screen (1.3.2 / 2.4.3). That flip also opened
    // the list scrolled to its end (the newest); the list is scrolled there here instead, on arrival and when one is added
    const listRef = useRef(null);
    const comments = [...props.selectedRequest.comments].reverse();
    useLayoutEffect(() => {
        if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
    }, [comments.length]);

    // Review of ticket 21 (2.1.1): at desktop width the list scrolls inside its card, and with only text in it nothing
    // inside takes focus, so the keyboard could not scroll it. As a table's region (tickets 16 / 22, ruling Q37): the list
    // is a region named by the card's heading, and a Tab stop exactly while it overflows (Util/dataTable.jsx). Re-measured
    // when it or its comments change size. It never scrolls sideways (overflow-x hidden), so only its height counts
    useEffect(() => {
        const list = listRef.current;
        return list ? tabStopWhileScrolling(list, [...list.children]) : undefined;
    }, [comments.length]);

    const openModal = () => {
        dialogSession.renew();
        setIsAddNewModalVisible(true);
    };

    const closeModalHandler = () => {
        dialogSession.renew(); // a reply still on its way belongs to a closed dialog now
        setNewComment(EMPTY_COMMENT);
        setFieldErrors({});
        setFocusRequest(null);
        setIsAddingComment(false);
        setIsAddNewModalVisible(false);
        // RCMC-18 / RCMC-01: the form's own messages leave with it ("required field is empty", a failure, "chosen")
        messages.clearAlert();
        messages.clearStatus();
        if (inputRef.current) inputRef.current.value = "";
    }

    /** The comment's text; once it has some, its error goes (the next Add checks it again) */
    const setCommentText = (text) => {
        setNewComment((prevState) => ({ ...prevState, comment: text }));
        if (text.trim() !== "") setFieldErrors((prevErrors) => ({ ...prevErrors, comment: undefined }));
    };

    /** RCMC-01: forget the chosen attachment (input, form state, its "chosen" message); `error` goes on its control */
    const dropAttachment = (error) => {
        if (inputRef.current) inputRef.current.value = ""; // so choosing the same file again still fires a change
        messages.clearStatus();
        setNewComment((prevState) => ({ ...prevState, attachment: "" }));
        setFieldErrors((prevErrors) => ({ ...prevErrors, attachment: error }));
    };

    // RCMC-01 (ticket 07's rules): a picked file is checked for size and type at once (v1.2: alert() over 1 MB, any
    // type). A refused file is not kept: its error goes on the control and to the alert region (ruling Q29), and focus
    // stays on the control, where the picker left it. An accepted file is announced (ruling Q30's pattern).
    const chooseAttachment = (file) => {
        if (!file) return; // picker cancelled: keep what was chosen before (as v1.2)
        const error = supportingDocumentError(file);
        if (error) {
            dropAttachment(error);
            messages.alert(error, "file");
            return;
        }
        // The file's own refusal is out of date; anything else in the alert region (the summary) still holds
        messages.clearAlert("file");
        setNewComment((prevState) => ({ ...prevState, attachment: file }));
        setFieldErrors((prevErrors) => ({ ...prevErrors, attachment: undefined }));
        messages.status(`Attachment chosen — ${file.name}.`);
    };

    // RCMC-01: Remove clears the file and puts focus back on the upload control (the button itself goes)
    const removeAttachment = () => {
        const { name } = newComment.attachment;
        dropAttachment(undefined);
        inputRef.current.focus();
        messages.status(`Attachment removed — ${name}.`);
    };

    /** RCMC-18: the Comment field in error, announced, with focus on it */
    const commentInError = (error, announcement) => {
        setFieldErrors((prevErrors) => ({ ...prevErrors, comment: error }));
        messages.alert(announcement, "form");
        setFocusRequest({ id: COMMENT_FIELD.id });
    };

    const addNewComment = () => {
        // RCMC-18 (v1.2: alert()): a blank comment sends nothing. The field shows its error, the alert region says
        // which field is empty (ruling Q25's summary), and focus goes to it
        if(newComment.comment.trim() === ""){
            commentInError(COMMENT_FIELD.error, `1 required field is empty — ${COMMENT_FIELD.label}.`);
            return;
        }
        // The comment is written, so an earlier summary is out of date; this attempt's outcome is announced below
        messages.clearAlert();
        // The payload is v1.2's: the message, the file (or ""), and its name and size as the page shows them
        const formData = new FormData();
        formData.append("message",newComment.comment);
        formData.append("attachment",newComment.attachment);
        if(newComment.attachment !== ""){
            formData.append("attachment_name",newComment.attachment.name);
            formData.append("attachment_size",getFileSize(newComment.attachment));
        } else {
            formData.append("attachment_name","");
            formData.append("attachment_size","");
        }
        setIsAddingComment(true);
        const fromThisDialog = dialogSession.capture();
        axios.post("/api/user/service-request-details/"+requestId+"/new-comment",formData,{headers: {"Content-Type": "multipart/form-data",Authorization: "Bearer " + authCtx.authTokens.access}}).then(response => {
            // The dialog that sent it closes; one closed (or opened again) since is left as it is
            if (fromThisDialog()) closeModalHandler();
            props.setSelectedRequest(prevState => {return {...prevState,comments:[response.data.comment,...prevState.comments]}});
            // RCMC-17 (v1.2: nothing said): the outcome, in the status region
            globalCtx.raiseAlert(`Comment added to ${requestId}.`, "success");
        }).catch(e => {
            // RCMC-17 / RCMC-14 (v1.2: alert() "Error occurred while uploading the file", whatever went wrong)
            const message = errorMessage(e);
            if (!fromThisDialog()) {
                // Closed (or opened again) mid-request: the alert is all that is left to say
                globalCtx.raiseAlert(message, "error");
                return;
            }
            setIsAddingComment(false);
            const field = e.response?.data?.field;
            if (field === "attachment") {
                // RCMC-01 (ruling Q29): the server refused the attachment (size, type or content). As a refusal in the
                // browser: the message goes on the control, the file is dropped, focus goes there
                dropAttachment(message);
                messages.alert(message, "file");
                setFocusRequest({ id: ATTACHMENT_ID });
            } else if (field === "message") {
                // RCMC-18: the server found the comment blank (its strip() and the browser's trim() differ on a few
                // characters): on the field, as the browser's own check
                commentInError(message, message);
            } else {
                messages.alert(message, "form");
            }
        });
    }

    // The comment field's error line, linked from the field by aria-describedby
    const commentError = fieldErrors.comment;
    const commentErrorId = `${COMMENT_FIELD.id}-error`;

  return (
    <CustomCard className="comments-card" styles={{overflow:"hidden",flex:"1"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",borderBottom:"1px solid #ddd"}}>
            {/* RCMC-08 (ticket 21): the card's title is a heading (it looks like one), with v1.2's look */}
            <h2 id={COMMENTS_HEADING_ID} className={styles['card-title']} style={{fontWeight:"bold",fontSize:"0.9rem",padding:"10px"}}>Comments</h2>
            {/* Q34 (ticket 21): v1.2's "+" was a <div onClick> — no Tab stop, no name ("clickable" to NVDA). A native button
                named for what it does; the icon is decoration. Focus comes back here when the dialog closes */}
            <button type="button" className={styles['add-comment']} aria-label="Add comment" onClick={openModal}>
                <IoAdd aria-hidden="true" focusable="false" size={16} />
            </button>
        </div>
        {/* A region named by the card's heading; a Tab stop while it scrolls (the effect above). Its focus ring is drawn on
            the card (static/portal-tokens.css), as a table region's is */}
        <div ref={listRef} className="comment-list" role="region" aria-labelledby={COMMENTS_HEADING_ID} style={{display:"flex",flexDirection:"column",overflowY:"auto",overflowX:"hidden"}}>
            {comments.length === 0 && <div style={{padding:"10px"}}>There are no comments on this issue yet.</div>}
            {comments.map((item) => (
                <div key={item.comment_id} className={`${styles['message']} ${authCtx.user.phone_no === item.last_updated_by.phone_no ? styles['message-from-user']:styles['message-to-user']}`}>
                    <span className={styles['sender-details']}>{item.last_updated_by.name} , {item.last_updated_on}</span>
                    <div className={styles['chat-body']}>
                        <div className={styles['chat-message']}><pre style={{whiteSpace:"pre-line"}}>{item.message}</pre></div>
                        {item.attachment && <div className={styles['attachment-container']}>
                            <FaImage aria-hidden="true" size={24} style={{marginRight:"10px",color:"#ddd"}} />
                            <div style={{flex:"1"}}>
                                <div className={styles['attachment-name']}>{item.attachment_name}</div>
                                <div className={styles['attachment-size']}>{item.attachment_size}</div>
                            </div>
                            {/* RCMC-12 (ticket 21): named for the file it fetches (v1.2: an icon with no name) */}
                            {/* Ticket 22 (Q35): busy without being disabled, so focus stays on it while the file comes (Util/busyButton.js) */}
                            <Button className='attachment-button' aria-label={`Download ${item.attachment_name}`} {...busyButtonProps(props.isDownloading(item.comment_id), () => {props.downloadFile(item.comment_id,item.attachment,item.attachment_name)})} style={{marginLeft:"10px",color:"var(--portal-primary)",background:"transparent",border:"none",padding:"0px"}}>{!props.isDownloading(item.comment_id) && <MdDownload aria-hidden="true" size={24} />}</Button>
                        </div>}
                    </div>
                </div>
            ))}
        </div>
        <Dialog header="Add new comment" visible={isAddNewModalVisible} style={{ width: "50vw" }} onHide={closeModalHandler} {...commentDialogIsolation}>
            {/* RCMC-18: the key to the "*" on the Comment label (the "*" itself is hidden from screen readers; the field is
                announced as required instead) — Raise Service Request's pattern (ticket 06) */}
            <p className={formStyles["required-key"]}>* Required</p>
            {/* No bottom margin, as in v1.2: the upload row sits directly below */}
            <div>
                <FloatLabel>
                    <InputTextarea id={COMMENT_FIELD.id} value={newComment.comment} onChange={(e) => setCommentText(e.target.value)} rows={5} cols={30}
                        aria-required="true"
                        aria-invalid={commentError ? "true" : undefined}
                        aria-describedby={commentError ? commentErrorId : undefined}
                        className={commentError ? "p-invalid" : undefined} />
                    <label htmlFor={COMMENT_FIELD.id}>{COMMENT_FIELD.label}<span className={formStyles["required-mark"]} aria-hidden="true">*</span></label>
                </FloatLabel>
                {commentError && <div id={commentErrorId} className={formStyles["field-error"]}>{commentError}</div>}
            </div>
            {/* RCMC-01: v1.2's "Attachments ( Upto 1 MB )" div over a hidden input → ticket 07's control: a native file
                input named by its label, the accepted types, the 2 MB limit as its description */}
            <SupportingDocumentUpload
              id={ATTACHMENT_ID}
              label="Upload attachment"
              inputRef={inputRef}
              file={newComment.attachment}
              error={fieldErrors.attachment}
              onChoose={chooseAttachment}
              onRemove={removeAttachment}
            />
            {/* Ticket 22 (Q35): busy without being disabled, so focus stays on it (Util/busyButton.js) */}
            <Button label="Add" raised style={{width:"100%"}} {...busyButtonProps(isAddingComment, addNewComment)} />
        </Dialog>
    </CustomCard>
  )
}

export default Comments
